<template>
  <div class="page-container">
    <Header title="Types de congés" />
    <div class="conges-view">
      <div class="container">
        <!-- En-tête avec bouton retour -->
        <div class="view-header">
          <router-link to="/parametres" class="back-button">
            <span class="material-icons">arrow_back</span>
            Retour aux paramètres
          </router-link>
          <h1>Types de congés</h1>
          <div class="header-actions">
            <button class="btn btn-primary" @click="openAddCongeModal">
              <span class="material-icons">add</span>
              Nouveau type de congé
            </button>
          </div>
        </div>

        <!-- Section de recherche -->
        <div class="search-section">
          <div class="search-input">
            <span class="material-icons">search</span>
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="Rechercher un type de congé..." 
              @input="filterConges"
            />
          </div>
        </div>

        <!-- Liste des types de congés -->
        <div class="content-container">
          <div v-if="loading" class="loading-indicator">
            <div class="spinner"></div>
            <p>Chargement des types de congés...</p>
          </div>
          
          <div v-else-if="filteredConges.length === 0" class="no-results">
            <span class="material-icons">search_off</span>
            <p v-if="searchQuery">Aucun type de congé trouvé pour "{{ searchQuery }}"</p>
            <p v-else>Aucun type de congé disponible. Veuillez en ajouter un.</p>
          </div>

          <div v-else class="conges-list">
            <div
              v-for="conge in paginatedConges"
              :key="conge.id"
              class="conge-item"
              :class="{ 'selected': selectedConge && selectedConge.id === conge.id }"
              @click="selectConge(conge)"
            >
              <div class="conge-info">
                <span class="conge-icon material-icons">event_available</span>
                <div class="conge-details">
                  <h3>{{ conge.libelle }}</h3>
                  <p>
                    <span class="conge-code">{{ conge.code }}</span>
                    <span class="conge-color-dot" :style="{ backgroundColor: conge.couleur }"></span>
                  </p>
                </div>
              </div>
              <div class="conge-actions">
                <button class="btn-icon" @click.stop="editConge(conge)">
                  <span class="material-icons">edit</span>
                </button>
                <button class="btn-icon" @click.stop="confirmDeleteConge(conge)">
                  <span class="material-icons">delete</span>
                </button>
              </div>
            </div>
          </div>
          
          <!-- Pagination -->
          <div v-if="filteredConges.length > 0" class="pagination-container">
            <div class="pagination-info">
              Affichage de {{ startIndex + 1 }}-{{ endIndex }} sur {{ filteredConges.length }} types de congés
            </div>
            <div class="pagination-controls">
              <button 
                class="pagination-btn" 
                :disabled="currentPage === 1" 
                @click="goToPage(1)"
                :class="{ 'disabled': currentPage === 1 }"
              >
                <span class="material-icons">first_page</span>
              </button>
              <button 
                class="pagination-btn" 
                :disabled="currentPage === 1" 
                @click="goToPage(currentPage - 1)"
                :class="{ 'disabled': currentPage === 1 }"
              >
                <span class="material-icons">chevron_left</span>
              </button>
              
              <div class="pagination-pages">
                <button 
                  v-for="page in displayedPages" 
                  :key="page" 
                  class="pagination-page" 
                  :class="{ 'active': currentPage === page }"
                  @click="goToPage(page)"
                >
                  {{ page }}
                </button>
              </div>
              
              <button 
                class="pagination-btn" 
                :disabled="currentPage === totalPages" 
                @click="goToPage(currentPage + 1)"
                :class="{ 'disabled': currentPage === totalPages }"
              >
                <span class="material-icons">chevron_right</span>
              </button>
              <button 
                class="pagination-btn" 
                :disabled="currentPage === totalPages" 
                @click="goToPage(totalPages)"
                :class="{ 'disabled': currentPage === totalPages }"
              >
                <span class="material-icons">last_page</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Détails du type de congé sélectionné -->
        <div v-if="selectedConge" class="conge-details-panel">
          <div class="panel-header">
            <h3>Détails du type de congé</h3>
            <button class="btn-close" @click="selectedConge = null">
              <span class="material-icons">close</span>
            </button>
          </div>
<div class="panel-content enhanced-detail-panel">
  <div class="detail-field icon-field">
    <span class="material-icons detail-icon">description</span>
    <span class="field-label">Libellé:</span>
    <span class="field-value">{{ selectedConge.libelle }}</span>
  </div>
  <div class="detail-field icon-field">
    <span class="material-icons detail-icon">code</span>
    <span class="field-label">Code:</span>
    <span class="field-value">{{ selectedConge.code }}</span>
  </div>
  <div class="detail-field icon-field">
    <span class="material-icons detail-icon">palette</span>
    <span class="field-label">Couleur:</span>
    <div class="color-preview">
      <span class="color-box" :style="{ backgroundColor: selectedConge.couleur }"></span>
      <span>{{ selectedConge.couleur }}</span>
    </div>
  </div>
  <div class="detail-field icon-field">
    <span class="material-icons detail-icon">attach_money</span>
    <span class="field-label">Est payé:</span>
    <span class="field-value">{{ selectedConge.estPaye ? 'Oui' : 'Non' }}</span>
  </div>
  <div class="detail-field icon-field">
    <span class="material-icons detail-icon">done_outline</span>
    <span class="field-label">Compte dans le quota:</span>
    <span class="field-value">{{ selectedConge.compteDansQuota ? 'Oui' : 'Non' }}</span>
  </div>
  <div class="detail-field icon-field">
    <span class="material-icons detail-icon">receipt_long</span>
    <span class="field-label">Nécessite un justificatif:</span>
    <span class="field-value">{{ selectedConge.necessiteJustificatif ? 'Oui' : 'Non' }}</span>
  </div>
  <div class="detail-field icon-field">
    <span class="material-icons detail-icon">verified_user</span>
    <span class="field-label">Actif:</span>
    <span class="field-value">{{ selectedConge.actif ? 'Oui' : 'Non' }}</span>
  </div>
  <div class="detail-field icon-field">
    <span class="material-icons detail-icon">access_time</span>
    <span class="field-label">Créé le:</span>
    <span class="field-value">{{ formatDate(selectedConge.createdAt) }}</span>
  </div>
  <div class="detail-field icon-field">
    <span class="material-icons detail-icon">update</span>
    <span class="field-label">Modifié le:</span>
    <span class="field-value">{{ formatDate(selectedConge.updatedAt) }}</span>
          </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal pour l'ajout/modification de type de congé -->
    <dialog ref="congeModal" class="conge-modal modal-centered">
      <div class="modal-header">
        <h3>{{ currentAction === 'create' ? 'Ajouter un type de congé' : 'Modifier le type de congé' }}</h3>
        <button class="btn-close" @click="closeModal">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="saveConge" id="congeForm">
          <div class="form-group">
            <label for="libelle">Libellé du type de congé*</label>
            <input 
              type="text" 
              id="libelle" 
              v-model="formData.libelle" 
              required 
              :class="{ 'error': formErrors.libelle }"
            />
            <span class="error-message" v-if="formErrors.libelle">{{ formErrors.libelle }}</span>
          </div>
          <div class="form-group">
            <label for="code">Code*</label>
            <input 
              type="text" 
              id="code" 
              v-model="formData.code" 
              required 
              maxlength="5"
              :class="{ 'error': formErrors.code }"
            />
            <span class="error-message" v-if="formErrors.code">{{ formErrors.code }}</span>
          </div>
          <div class="form-group">
            <label for="couleur">Couleur</label>
            <div class="color-picker-container">
              <input 
                type="color" 
                id="couleur" 
                v-model="formData.couleur" 
                class="color-picker"
              />
              <input 
                type="text" 
                v-model="formData.couleur" 
                class="color-text"
              />
            </div>
          </div>
          <div class="form-options">
            <div class="form-group checkbox-group">
              <label class="checkbox-container">
                <input type="checkbox" v-model="formData.estPaye">
                <span class="checkmark"></span>
                Est payé
              </label>
            </div>
            <div class="form-group checkbox-group">
              <label class="checkbox-container">
                <input type="checkbox" v-model="formData.compteDansQuota">
                <span class="checkmark"></span>
                Compte dans le quota
              </label>
            </div>
            <div class="form-group checkbox-group">
              <label class="checkbox-container">
                <input type="checkbox" v-model="formData.necessiteJustificatif">
                <span class="checkmark"></span>
                Nécessite un justificatif
              </label>
            </div>
            <div class="form-group checkbox-group">
              <label class="checkbox-container">
                <input type="checkbox" v-model="formData.actif">
                <span class="checkmark"></span>
                Actif
              </label>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="closeModal">Annuler</button>
<button class="btn btn-primary" form="congeForm" :disabled="savingConge">
  <div v-if="savingConge" class="btn-spinner"></div>
  <span v-if="savingConge">Traitement en cours...</span>
  <span v-else>{{ currentAction === 'create' ? 'Ajouter' : 'Enregistrer' }}</span>
        </button>
      </div>
    </dialog>

    <!-- Modal de confirmation de suppression -->
    <dialog ref="confirmDeleteModal" class="confirm-modal modal-centered">
      <div class="modal-header">
        <h3>Confirmer la suppression</h3>
        <button class="btn-close" @click="closeConfirmModal">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body">
        <div class="confirmation-icon">
          <span class="material-icons">warning</span>
        </div>
        <div class="confirmation-content">
          <p>Êtes-vous sûr de vouloir supprimer le type de congé "{{ congeToDelete?.libelle }}" ?</p>
          <p class="warning">Cette action est irréversible et peut affecter les demandes de congés associées.</p>
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="closeConfirmModal">Annuler</button>
        <button class="btn btn-danger" @click="deleteConge">Supprimer</button>
      </div>
    </dialog>

    <!-- Notification toast -->
    <div 
      v-if="notification.show" 
      class="notification" 
      :class="notification.type"
    >
      <div class="notification-icon">
        <span class="material-icons">
          {{ notification.type === 'success' ? 'check_circle' : 
             notification.type === 'error' ? 'error' : 
             notification.type === 'info' ? 'info' : 'warning' }}
        </span>
      </div>
      <div class="notification-content">
        <h4>{{ notification.title }}</h4>
        <p>{{ notification.message }}</p>
      </div>
      <button class="notification-close" @click="notification.show = false">
        <span class="material-icons">close</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue';
import api from '../services/api';
import Header from '../components/Header.vue';

const conges = ref([]);
const filteredConges = ref([]);
const selectedConge = ref(null);
const loading = ref(false);
const searchQuery = ref('');

// Pagination
const currentPage = ref(1);
const itemsPerPage = ref(10);
const totalPages = computed(() => Math.ceil(filteredConges.value.length / itemsPerPage.value));
const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage.value);
const endIndex = computed(() => Math.min(startIndex.value + itemsPerPage.value, filteredConges.value.length));
const paginatedConges = computed(() => {
  return filteredConges.value.slice(startIndex.value, endIndex.value);
});

// Affichage des pages pour la pagination
const displayedPages = computed(() => {
  const totalPagesCount = totalPages.value;
  const current = currentPage.value;
  const delta = 2; // Nombre de pages à afficher avant et après la page courante
  
  let pages = [];
  
  // Toujours inclure la première page
  pages.push(1);
  
  // Calculer la plage de pages à afficher
  let start = Math.max(2, current - delta);
  let end = Math.min(totalPagesCount - 1, current + delta);
  
  // Ajouter des ellipses si nécessaire
  if (start > 2) {
    pages.push('...');
  }
  
  // Ajouter les pages intermédiaires
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  
  // Ajouter des ellipses si nécessaire
  if (end < totalPagesCount - 1) {
    pages.push('...');
  }
  
  // Toujours inclure la dernière page si elle existe
  if (totalPagesCount > 1) {
    pages.push(totalPagesCount);
  }
  
  return pages;
});

// Modals
const congeModal = ref(null);
const confirmDeleteModal = ref(null);
const activeModal = ref(null); // Pour suivre quelle modale est active

// État du formulaire
const formData = reactive({
  id: null,
  libelle: '',
  code: '',
  couleur: '#9C27B0',
  estPaye: true,
  compteDansQuota: true,
  necessiteJustificatif: false,
  actif: true
});
const formErrors = reactive({
  libelle: '',
  code: ''
});

// État de l'action courante (create, update, delete)
const currentAction = ref('create');
const congeToDelete = ref(null);

// Notification
const notification = reactive({
  show: false,
  type: 'info',
  title: '',
  message: '',
  duration: 5000
});

// Surveiller les événements de fermeture des modales
watch(activeModal, (newVal, oldVal) => {
  if (oldVal && !newVal) {
    document.body.classList.remove('modal-open');
  }
});

onMounted(async () => {
  await loadConges();
});

async function loadConges() {
  loading.value = true;
  try {
    const response = await api.getCongesTypes();
    if (response.data && response.data.success) {
      conges.value = response.data.data || [];
      filteredConges.value = [...conges.value];
    }
  } catch (error) {
    console.error('Erreur lors du chargement des types de congés:', error);
    showNotification('error', 'Erreur', 'Impossible de charger les types de congés.');
  } finally {
    loading.value = false;
  }
}

function filterConges() {
  if (!searchQuery.value.trim()) {
    filteredConges.value = [...conges.value];
  } else {
    const query = searchQuery.value.toLowerCase();
    filteredConges.value = conges.value.filter(conge => {
      return conge.libelle.toLowerCase().includes(query) || 
             conge.code.toLowerCase().includes(query);
    });
  }
  
  // Réinitialiser la pagination quand on filtre
  currentPage.value = 1;
}

function selectConge(conge) {
  selectedConge.value = conge;
}

function goToPage(page) {
  if (page === '...') return;
  currentPage.value = page;
  // Faire défiler vers le haut de la liste
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function openAddCongeModal() {
  // Fermer toute modale active
  if (activeModal.value) {
    activeModal.value.close();
  }
  
  resetForm();
  currentAction.value = 'create';
  
  // Définir la nouvelle modale active et l'ouvrir
  setTimeout(() => {
    activeModal.value = congeModal.value;
    document.body.classList.add('modal-open');
    congeModal.value.showModal();
  }, 100);
}

function editConge(conge) {
  // Fermer toute modale active
  if (activeModal.value) {
    activeModal.value.close();
  }
  
  currentAction.value = 'edit';
  
  formData.id = conge.id;
  formData.libelle = conge.libelle;
  formData.code = conge.code;
  formData.couleur = conge.couleur;
  formData.estPaye = conge.estPaye;
  formData.compteDansQuota = conge.compteDansQuota;
  formData.necessiteJustificatif = conge.necessiteJustificatif;
  formData.actif = conge.actif;
  
  // Définir la nouvelle modale active et l'ouvrir
  setTimeout(() => {
    activeModal.value = congeModal.value;
    document.body.classList.add('modal-open');
    congeModal.value.showModal();
  }, 100);
}

function confirmDeleteConge(conge) {
  // Fermer toute modale active
  if (activeModal.value) {
    activeModal.value.close();
  }
  
  // Stocker le congé à supprimer
  congeToDelete.value = conge;
  
  // Définir la nouvelle modale active et l'ouvrir
  setTimeout(() => {
    activeModal.value = confirmDeleteModal.value;
    document.body.classList.add('modal-open');
    confirmDeleteModal.value.showModal();
  }, 100);
}

function closeModal() {
  document.body.classList.remove('modal-open');
  activeModal.value = null;
  congeModal.value.close();
}

function closeConfirmModal() {
  document.body.classList.remove('modal-open');
  activeModal.value = null;
  confirmDeleteModal.value.close();
}

function resetForm() {
  formData.id = null;
  formData.libelle = '';
  formData.code = '';
  formData.couleur = '#30C7B5';
  formData.estPaye = true;
  formData.compteDansQuota = true;
  formData.necessiteJustificatif = false;
  formData.actif = true;
  
  formErrors.libelle = '';
  formErrors.code = '';
}

const savingConge = ref(false);

async function saveConge() {
  if (!validateForm()) return;
  
  savingConge.value = true;
  
  try {
    let response;
    
    if (currentAction.value === 'create') {
      response = await api.createCongeType(formData);
      if (response.data && response.data.success) {
        showNotification('success', 'Succès', 'Type de congé créé avec succès');
      }
    } else {
      response = await api.updateCongeType(formData.id, formData);
      if (response.data && response.data.success) {
        showNotification('success', 'Succès', 'Type de congé modifié avec succès');
      }
    }
    
    await loadConges();
    closeModal();
    
    if (selectedConge.value && selectedConge.value.id === formData.id) {
      const updatedConge = conges.value.find(c => c.id === formData.id);
      if (updatedConge) {
        selectedConge.value = updatedConge;
      }
    }
    
  } catch (error) {
    console.error('Erreur lors de la sauvegarde du type de congé:', error);
    showNotification('error', 'Erreur', `Impossible de sauvegarder le type de congé: ${error.message}`);
  } finally {
    savingConge.value = false;
  }
}
async function deleteConge() {
  if (!congeToDelete.value) return;
  
  try {
    const response = await api.deleteCongeType(congeToDelete.value.id);
    if (response.data && response.data.success) {
      showNotification('success', 'Suppression', 'Type de congé supprimé avec succès');
      
      if (selectedConge.value && selectedConge.value.id === congeToDelete.value.id) {
        selectedConge.value = null;
      }
      
      await loadConges();
    }
  } catch (error) {
    console.error('Erreur lors de la suppression du type de congé:', error);
    showNotification('error', 'Erreur', `Impossible de supprimer le type de congé: ${error.message}`);
  }
  
  closeConfirmModal();
}

function validateForm() {
  let isValid = true;
  
  // Validation du libellé
  if (!formData.libelle.trim()) {
    formErrors.libelle = 'Le libellé est requis';
    isValid = false;
  } else if (formData.libelle.length > 100) {
    formErrors.libelle = 'Le libellé ne doit pas dépasser 100 caractères';
    isValid = false;
  } else {
    formErrors.libelle = '';
  }
  
  // Validation du code
  if (!formData.code.trim()) {
    formErrors.code = 'Le code est requis';
    isValid = false;
  } else if (formData.code.length > 5) {
    formErrors.code = 'Le code ne doit pas dépasser 5 caractères';
    isValid = false;
  } else {
    formErrors.code = '';
  }
  
  return isValid;
}

function showNotification(type, title, message) {
  notification.show = true;
  notification.type = type;
  notification.title = title;
  notification.message = message;
  
  setTimeout(() => {
    notification.show = false;
  }, notification.duration);
}

function formatDate(dateString) {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
}
</script>

<style scoped>
/* Import des variables de couleurs OpSolver */
@import '@/assets/css/variables.css';

.conges-view {
  padding: 20px;
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
}

.container {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  padding: 20px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

@media (min-width: 992px) {
  .container {
    grid-template-columns: 2fr 1fr;
  }
  
  .view-header, .search-section, .loading-indicator, .no-results {
    grid-column: span 2;
  }
}

/* En-tête */
.view-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
}

.view-header h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

.back-button {
  display: flex;
  align-items: center;
  color: var(--turquoise-primary);
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
}

.back-button:hover {
  color: var(--turquoise-deep);
  transform: translateX(-3px);
}

.back-button .material-icons {
  margin-right: 5px;
}

.header-actions {
  display: flex;
  gap: 10px;
}

/* Section de recherche */
.search-section {
  margin-bottom: 20px;
}

.search-input {
  display: flex;
  align-items: center;
  background-color: #f5f5f5;
  border-radius: 4px;
  padding: 10px 15px;
}

.search-input .material-icons {
  color: var(--turquoise-primary);
  margin-right: 10px;
}

.search-input input {
  border: none;
  background: transparent;
  flex: 1;
  font-size: 16px;
  outline: none;
}

.search-input:focus-within {
  border-color: var(--turquoise-primary);
}

/* Liste des types de congés */
.conges-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.conge-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-radius: 8px;
  background-color: #f9f9f9;
  border-left: 4px solid transparent;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.conge-item:hover {
  background-color: rgba(48, 199, 181, 0.05);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.conge-item.selected {
  border-left: 4px solid var(--turquoise-primary);
  background-color: var(--turquoise-pale);
}

.conge-info {
  display: flex;
  align-items: center;
}

.conge-icon {
  color: var(--turquoise-primary);
  margin-right: 15px;
  font-size: 24px;
}

.conge-details h3 {
  margin: 0 0 5px;
  font-size: 16px;
  font-weight: 500;
}

.conge-details p {
  margin: 0;
  color: #777;
  font-size: 14px;
}

.conge-actions {
  display: flex;
  gap: 10px;
}

.btn-icon {
  border: none;
  background: none;
  color: #777;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.btn-icon:hover {
  background-color: rgba(48, 199, 181, 0.1);
  color: var(--turquoise-deep);
}

/* Panneau de détails */
.conge-details-panel {
  background-color: #f9f9f9;
  border-radius: 8px;
  overflow: hidden;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: var(--turquoise-pale);
  padding: 15px;
  border-bottom: 1px solid #ddd;
}

.panel-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
}

.panel-content {
  padding: 15px;
}

.detail-field {
  margin-bottom: 12px;
}

.field-label {
  display: block;
  font-size: 14px;
  color: #777;
  margin-bottom: 3px;
}

.field-value {
  font-size: 16px;
  color: #333;
}

/* Indicateur de chargement */
.loading-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid var(--turquoise-primary);
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Pas de résultats */
.no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
  color: #777;
}

.no-results .material-icons {
  font-size: 48px;
  margin-bottom: 15px;
  color: var(--turquoise-primary);
  opacity: 0.6;
}

/* Modal */
/* Suppression de .modal-centered car déjà défini dans les styles spécifiques */

.conge-modal, .confirm-modal {
  border: none;
  border-radius: 12px;
  padding: 0;
  max-width: 500px;
  width: 90%;
  max-height: 85vh; /* Limite la hauteur à 85% de la hauteur de la fenêtre */
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25), 0 5px 15px rgba(0, 0, 0, 0.15);
  animation: fadeIn 0.3s ease-out;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Styles pour les modales */
.conge-modal,
.confirm-modal {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  margin: 0;
}

/* Style pour empêcher l'ouverture simultanée de plusieurs modales */
dialog:not([open]) {
  display: none !important;
}

/* S'assurer que les modales ne se superposent pas */
dialog.conge-modal,
dialog.confirm-modal {
  position: fixed !important;
  z-index: auto !important;
}

/* Style pour le body quand une modale est ouverte */
body.modal-open {
  overflow: hidden;
}

/* Style spécifique pour la modale de confirmation */
.confirm-modal {
  z-index: 9999 !important; /* Force un z-index très élevé */
}

/* Styles pour garantir que la modale de confirmation est toujours au-dessus */
dialog.confirm-modal::backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 9998 !important;
}

/* Assurer que la modale de confirmation est toujours visible */
dialog.confirm-modal[open] {
  display: flex !important;
  flex-direction: column !important;
  z-index: 9999 !important;
  position: fixed !important;
  box-shadow: 0 0 0 100vmax rgba(0, 0, 0, 0.3) !important;
}

.conge-modal::before, .confirm-modal::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(to right, var(--turquoise-deep), var(--turquoise-primary));
}

.conge-modal::backdrop {
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
}

/* Le backdrop de la modale de confirmation est défini plus haut avec des styles plus spécifiques */

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 20px;
  background: linear-gradient(to right, var(--turquoise-deep), var(--turquoise-primary));
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  color: white;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: white;
}

.modal-body {
  padding: 20px;
  overflow-y: auto; /* Permet le défilement vertical */
  max-height: calc(85vh - 130px); /* Hauteur max en tenant compte de l'en-tête et du pied */
  scrollbar-width: thin;
  scrollbar-color: var(--turquoise-primary) #f0f0f0;
}

.modal-body::-webkit-scrollbar {
  width: 8px;
}

.modal-body::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb {
  background-color: var(--turquoise-primary);
  border-radius: 4px;
}

.modal-body form {
  padding-right: 5px;
}

.modal-footer {
  padding: 15px 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  border-top: 1px solid #eee;
  background-color: white;
  position: sticky;
  bottom: 0;
}

.btn-close {
  background: none;
  border: none;
  cursor: pointer;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.2s ease;
  margin: -8px;
}

.btn-close:hover {
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
  transform: rotate(90deg);
}

/* Formulaire */
.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
}

.form-group input, .form-group select, .form-group textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-group input:focus, .form-group select:focus, .form-group textarea:focus {
  border-color: var(--turquoise-primary);
  outline: none;
  box-shadow: 0 0 0 3px rgba(48, 199, 181, 0.15);
}

.form-group input.error, .form-group select.error, .form-group textarea.error {
  border-color: var(--coral-soft);
}

.error-message {
  color: var(--coral-soft);
  font-size: 12px;
  margin-top: 5px;
}

/* Boutons */
.btn {
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px !important;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border: none;
  transition: background-color 0.2s;
}

.btn .material-icons {
  font-size: 20px !important;
}

.btn-primary {
  background-color: var(--turquoise-primary);
  color: white;
  border: 1px solid var(--turquoise-primary);
  padding: 10px 18px;
  font-weight: 500;
  letter-spacing: 0.3px;
  transition: all 0.3s ease;
  font-size: 14px !important;
}

.btn-primary:hover {
  background-color: var(--turquoise-deep);
  box-shadow: 0 4px 8px rgba(48, 199, 181, 0.2);
  transform: translateY(-1px);
}

.btn-primary:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(48, 199, 181, 0.1);
}

.btn-secondary {
  background-color: #f5f5f5;
  color: var(--turquoise-dark);
  border: 1px solid #e0e0e0;
  padding: 10px 18px;
  font-weight: 500;
  letter-spacing: 0.3px;
  transition: all 0.3s ease;
}

.btn-secondary:hover {
  background-color: #e8e8e8;
  border-color: #d0d0d0;
  color: var(--turquoise-deep);
}

.btn-secondary:active {
  transform: translateY(1px);
  background-color: #e0e0e0;
}

.btn-danger {
  background-color: var(--coral-soft);
  color: white;
  border: 1px solid var(--coral-soft);
}

.btn-danger:hover {
  background-color: #ff6a56;
  box-shadow: 0 4px 8px rgba(255, 122, 104, 0.2);
  transform: translateY(-1px);
}

/* Notification */
.notification {
  position: fixed;
  bottom: 20px;
  right: 20px;
  min-width: 300px;
  max-width: 400px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 15px;
  display: flex;
  align-items: flex-start;
  gap: 15px;
  z-index: 1000;
  animation: slideIn 0.3s ease-out forwards;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.notification.success {
  border-left: 4px solid var(--turquoise-primary);
}

.notification.error {
  border-left: 4px solid var(--coral-soft);
}

.notification.warning {
  border-left: 4px solid var(--coral-soft);
}

.notification.info {
  border-left: 4px solid var(--turquoise-dark);
}

.notification-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification.success .material-icons {
  color: var(--turquoise-primary);
}

.notification.error .material-icons {
  color: var(--coral-soft);
}

.notification.warning .material-icons {
  color: var(--coral-soft);
}

.notification.info .material-icons {
  color: var(--turquoise-dark);
}

.notification-content {
  flex: 1;
}

.notification-content h4 {
  margin: 0 0 5px;
  font-size: 16px;
  font-weight: 600;
}

.notification-content p {
  margin: 0;
  font-size: 14px;
  color: #555;
}

.notification-close {
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: -5px;
}

.notification-close:hover {
  color: #333;
}

.warning {
  color: var(--coral-soft);
  font-weight: 500;
}

/* Styles pour les détails des congés */
.conge-code {
  background-color: #f0f0f0;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  margin-right: 8px;
}

.conge-color-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-left: 5px;
  vertical-align: middle;
}

.color-preview {
  display: flex;
  align-items: center;
  gap: 10px;
}

.color-box {
  display: inline-block;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid #ddd;
}

/* Styles pour le sélecteur de couleur */
.color-picker-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.color-picker {
  width: 50px;
  height: 40px;
  padding: 0;
  border: none;
  cursor: pointer;
}

.color-text {
  flex: 1;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

/* Styles pour les checkboxes */
.form-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-top: 15px;
}

.checkbox-container {
  display: flex;
  align-items: center;
  position: relative;
  padding-left: 35px;
  margin-bottom: 12px;
  cursor: pointer;
  font-size: 14px;
  user-select: none;
}

.checkbox-container input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkmark {
  position: absolute;
  top: 0;
  left: 0;
  height: 20px;
  width: 20px;
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.page-container {
  width: 100%;
  min-height: 100vh;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
}

.checkbox-container:hover input ~ .checkmark {
  background-color: #e0e0e0;
}

.checkbox-container input:checked ~ .checkmark {
  background-color: #9C27B0;
  border-color: #9C27B0;
}

.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

.checkbox-container input:checked ~ .checkmark:after {
  display: block;
}

.checkbox-container .checkmark:after {
  left: 7px;
  top: 3px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

/* Styles pour la boîte de confirmation */
.confirmation-icon {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

.confirmation-icon .material-icons {
  font-size: 48px;
  color: var(--turquoise-primary);
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.confirmation-content {
  text-align: center;
}


/* Styles de pagination */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #eee;
  flex-wrap: wrap;
  gap: 15px;
}

.pagination-info {
  color: #666;
  font-size: 14px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 5px;
}

.pagination-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #ddd;
  background-color: white;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.pagination-btn:hover:not(.disabled) {
  background-color: #f5f5f5;
  border-color: #9C27B0;
  color: #9C27B0;
}

.pagination-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-pages {
  display: flex;
  align-items: center;
  gap: 5px;
  margin: 0 5px;
}

.pagination-page {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0 8px;
  border: 1px solid #ddd;
  background-color: white;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.pagination-page:hover:not(.active) {
  background-color: #f5f5f5;
  border-color: #9C27B0;
  color: #9C27B0;
}

.pagination-page.active {
  background-color: var(--turquoise-primary);
  color: white;
  border-color: var(--turquoise-primary);
}
/* Styles des checkboxes personnalisés */
.checkbox-container {
  display: flex;
  align-items: center;
  position: relative;
  padding-left: 35px;
  margin-bottom: 12px;
  cursor: pointer;
  font-size: 15px;
  user-select: none;
}

.checkbox-container input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkmark {
  position: absolute;
  top: 0;
  left: 0;
  height: 22px;
  width: 22px;
  background-color: #fff;
  border: 2px solid #ddd;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.checkbox-container:hover input ~ .checkmark {
  border-color: var(--turquoise-primary);
}

.checkbox-container input:checked ~ .checkmark {
  background-color: var(--turquoise-primary);
  border-color: var(--turquoise-primary);
}

.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

.checkbox-container input:checked ~ .checkmark:after {
  display: block;
}

.checkbox-container .checkmark:after {
  left: 7px;
  top: 3px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.checkbox-container input:focus ~ .checkmark {
  box-shadow: 0 0 0 3px rgba(48, 199, 181, 0.2);
}

/* Styles améliorés pour le panneau de détails */
.enhanced-detail-panel {
  background: linear-gradient(135deg, #f8fdff 0%, #f1f9fc 100%);
  padding: 25px;
  border-radius: 12px;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.05);
}

.icon-field {
  display: grid;
  grid-template-columns: 32px 120px 1fr;
  align-items: center;
  gap: 15px;
  padding: 15px 0;
  border-bottom: 1px solid rgba(48, 199, 181, 0.1);
  transition: all 0.3s ease;
}

.icon-field:last-child {
  border-bottom: none;
}

.icon-field:hover {
  background-color: rgba(48, 199, 181, 0.03);
  border-radius: 8px;
  margin: 0 -10px;
  padding: 15px 10px;
}

.detail-icon {
  font-size: 20px !important;
  color: var(--turquoise-primary);
  background: rgba(48, 199, 181, 0.1);
  padding: 6px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  transition: all 0.3s ease;
}

.icon-field:hover .detail-icon {
  background: rgba(48, 199, 181, 0.2);
  transform: scale(1.1);
}

.icon-field .field-label {
  font-weight: 600;
  color: #2d3748;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
}

.icon-field .field-value {
  font-size: 15px;
  color: #4a5568;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.7);
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid rgba(48, 199, 181, 0.15);
  transition: all 0.3s ease;
}

.icon-field:hover .field-value {
  background: rgba(255, 255, 255, 0.9);
  border-color: rgba(48, 199, 181, 0.3);
  box-shadow: 0 2px 4px rgba(48, 199, 181, 0.1);
}

.icon-field .color-preview {
  background: rgba(255, 255, 255, 0.7);
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid rgba(48, 199, 181, 0.15);
  transition: all 0.3s ease;
}

.icon-field:hover .color-preview {
  background: rgba(255, 255, 255, 0.9);
  border-color: rgba(48, 199, 181, 0.3);
  box-shadow: 0 2px 4px rgba(48, 199, 181, 0.1);
}

/* Spinner pour les boutons */
.btn-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 8px;
}

.btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: none !important;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
